(function () {
    // save the section id from the script tag and find the section
    const sectionID = document.currentScript.dataset.id;
    const section = document.querySelector(`#${sectionID}`);
    if (!section) {
        console.warn(`Product section with ID ${sectionID} not found.`);
        return;
    }

    const inner = section.querySelector(".productV1Inner");
    const panel = section.querySelector(".productV1ContentPersonalize");
    if (!inner || !panel) {
        // personalize isn't enabled/rendered for this product
        return;
    }

    const productData = JSON.parse(section.querySelector(".productV1JSON").textContent);

    const toggleBtn = section.querySelector(".pdpPersonalizeToggle");
    const cancelBtn = section.querySelector(".pdpPersonalizeCancel");
    const personalizeATC = section.querySelector(".pdpPersonalizeATC");

    const patternPicker = section.querySelector(".pdpPersonalizePatternPicker");
    const uploadButton = section.querySelector(".pdpPersonalizeUploadButton");
    const uploadInput = section.querySelector(".pdpPersonalizeUploadInput");
    const uploadChip = section.querySelector(".pdpPersonalizeUploadChip");
    const uploadChipName = section.querySelector(".pdpPersonalizeUploadChipName");
    const engraveInput = section.querySelector(".pdpPersonalizeEngraveInput");
    const engraveCounter = section.querySelector(".pdpPersonalizeEngraveCounter");

    const cloudName = panel.dataset.cloudinaryCloud;
    const uploadPreset = panel.dataset.cloudinaryPreset;
    const engraveMaxWidthPct = parseFloat(panel.dataset.engraveMaxWidth) || 70;
    const engraveMaxHeightPct = parseFloat(panel.dataset.engraveMaxHeight) || 40;

    // base variant id -> the mirror variant that actually gets added to the cart (built in
    // pdp-personalize.liquid). The mirror carries the personalized price and its own SKU,
    // and is stocked only at the print-on-demand location, which is what makes Shopify
    // route the order there. A base variant missing from this map isn't personalizable.
    let mirrorVariants = {};
    try {
        mirrorVariants = JSON.parse(panel.querySelector(".pdpPersonalizeVariantMap").textContent);
    } catch (err) {
        console.error("Personalize: couldn't read the mirror variant map", err);
    }

    function mirrorFor(variantID) {
        return mirrorVariants[String(variantID)] || null;
    }

    const priceRealEl = section.querySelector(".pdpPersonalizeFooterPriceReal");
    const priceCompareEl = section.querySelector(".pdpPersonalizeFooterPriceCompare");

    const PRINT_SIZE = 2000;
    const MOCKUP_SIZE = 800;

    // fonts are metaobject-driven (Liquid marks the lowest sort_order as .active), so the
    // "default" font is read from whichever swatch actually rendered as active rather than
    // a hardcoded value that could drift out of sync with the merchant-configured order
    const defaultFontSwatch = section.querySelector(".pdpPersonalizeFontSwatch.active");
    const defaultFont = defaultFontSwatch
        ? {family: defaultFontSwatch.dataset.fontFamily, label: defaultFontSwatch.dataset.fontLabel, weight: parseInt(defaultFontSwatch.dataset.fontWeight, 10) || 400, file: defaultFontSwatch.dataset.fontFile}
        : {family: "'Bebas Neue', sans-serif", label: "Bebas Neue", weight: 400, file: ""}; // fallback if no fonts are configured yet

    /************
     Engraving auto-fit: grows to the max height first (font-size in px maps directly to a
     single line's height), then shrinks proportionally only if that overflows the max
     width - single line always, never wraps. Shared by the live preview and both canvas
     exports so all three render the exact same size instead of drifting apart.
     ************/
    const measureCtx = document.createElement("canvas").getContext("2d");

    function measureTextWidth(text, fontSize, fontFamily, weight) {
        measureCtx.font = `${weight} ${fontSize}px ${fontFamily}`;
        return measureCtx.measureText(text).width;
    }

    // fonts vary a lot in natural glyph width at the same nominal font-size, so "grow to
    // max height, only shrink if too wide" inherently leaves narrow fonts (which never
    // trigger the shrink) with generous margin while wide fonts get shrunk right up to
    // the edge of maxWidth. WIDTH_SAFETY reserves a consistent buffer so a wide font is
    // never shrunk flush against the limit - it won't equalize margins across every font
    // (that would mean *not* maximizing height, which contradicts growing to fill it
    // first), but it guarantees none of them end up with near-zero breathing room.
    // TRUE VERTICAL CENTRING. Canvas's textBaseline "middle" and CSS line-height:1 both centre
    // the EM box rather than the ink, so a caps-only string sits high - there are no descenders
    // to balance the ascent - while one with a descender sits low. The engraving should look
    // centred in the print area whatever the customer types, so every surface is placed from
    // the string's own ink box instead.
    //
    // measureText is the single source of truth for all four renderers (DOM preview, mockup
    // canvas, raster print file, outlined SVG). Deriving the SVG's offset from opentype's own
    // bounding box instead would re-introduce the drift this exists to remove: the two agree
    // closely but not exactly, and the customer approves the DOM preview.
    function inkMetrics(text, fontSize, fontFamily, weight) {
        measureCtx.font = `${weight} ${fontSize}px ${fontFamily}`;
        const m = measureCtx.measureText(text || "");
        const asc = m.actualBoundingBoxAscent;
        const desc = m.actualBoundingBoxDescent;
        const fbAsc = m.fontBoundingBoxAscent;
        const fbDesc = m.fontBoundingBoxDescent;
        const inkOk = Number.isFinite(asc) && Number.isFinite(desc);
        const fontOk = Number.isFinite(fbAsc) && Number.isFinite(fbDesc);
        return {
            // where the ink's centre sits relative to the baseline, y down
            inkFromBaseline: inkOk ? (desc - asc) / 2 : 0,
            // where the baseline sits relative to a line box centred at line-height:1, y down.
            // Half-leading cancels out, leaving (ascent - descent) / 2.
            baselineFromLineCentre: fontOk ? (fbAsc - fbDesc) / 2 : 0,
        };
    }

    const ENGRAVE_WIDTH_SAFETY = 0.88;

    function fitEngravingFontSize(text, maxWidth, maxHeight, fontFamily, weight) {
        const targetWidth = maxWidth * ENGRAVE_WIDTH_SAFETY;
        let fontSize = maxHeight;
        let width = measureTextWidth(text, fontSize, fontFamily, weight);
        if (width > targetWidth && width > 0) {
            fontSize *= targetWidth / width;
            width = measureTextWidth(text, fontSize, fontFamily, weight);
            if (width > targetWidth && width > 0) {
                fontSize *= targetWidth / width;
            }
        }
        return fontSize;
    }

    /************
     State
     ************/
    let activeTab = "design";
    let selectedPattern = null; // {name, handle, image}
    let uploadedPhoto = null; // {file, image, baseWidth, baseHeight}
    let cropState = {x: 0, y: 0, scale: 1};
    let engravingText = "";
    let selectedFont = defaultFont;

    /************
     Personalization is enabled per variant (each with its own mockup photo), so there can
     be several .pdpPersonalizeVisual blocks; these helpers always resolve to whichever one
     matches the currently selected variant (mirrors the theme's own per-variant gallery).
     ************/
    function activeVisual() {
        return section.querySelector(".pdpPersonalizeVisual.active");
    }

    function activeMockup() {
        return activeVisual()?.querySelector(".pdpPersonalizeVisualMockup");
    }

    function activeOverlay() {
        return activeVisual()?.querySelector(".pdpPersonalizeVisualOverlay");
    }

    function activeDecalArt() {
        return activeVisual()?.querySelector(".pdpPersonalizeVisualDecalArt");
    }

    function activeDecalText() {
        return activeVisual()?.querySelector(".pdpPersonalizeVisualDecalText");
    }

    function activeHitArea() {
        return activeVisual()?.querySelector(".pdpPersonalizeVisualHitArea");
    }

    /************
     Mask (product-level): a black & white image whose white region is the true shape of
     the print area. Loaded once, converted from luminance into a real alpha channel (so it
     works as a standard image mask/composite source regardless of whether the source PNG
     was exported fully opaque), and its white-region bounding box is detected - this
     replaces manually-tuned position/size settings entirely.
     ************/
    const assetsEl = section.querySelector(".pdpPersonalizeAssets");
    const maskSrc = assetsEl?.dataset.maskSrc;

    let maskAlphaCanvas = null;
    let maskBounds = {cx: 50, cy: 50, w: 60, h: 60}; // sensible fallback until the real mask loads

    function loadImage(src, crossOrigin) {
        return new Promise((resolve, reject) => {
            const image = new Image();
            if (crossOrigin) image.crossOrigin = crossOrigin;
            image.onload = () => resolve(image);
            image.onerror = reject;
            image.src = src;
        });
    }

    // rewrites the mask's alpha channel from its own luminance (so a fully-opaque black &
    // white PNG still works as a mask), and returns the white region's bounding box as a
    // percentage of the mask's own dimensions
    function buildAlphaMask(image) {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(image, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        let minX = canvas.width, maxX = 0, minY = canvas.height, maxY = 0;

        for (let y = 0; y < canvas.height; y++) {
            for (let x = 0; x < canvas.width; x++) {
                const i = (y * canvas.width + x) * 4;
                const luminance = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114) * (data[i + 3] / 255);
                data[i + 3] = luminance;
                if (luminance > 128) {
                    if (x < minX) minX = x;
                    if (x > maxX) maxX = x;
                    if (y < minY) minY = y;
                    if (y > maxY) maxY = y;
                }
            }
        }
        ctx.putImageData(imageData, 0, 0);

        const bounds = maxX >= minX && maxY >= minY
            ? {
                cx: ((minX + maxX) / 2 / canvas.width) * 100,
                cy: ((minY + maxY) / 2 / canvas.height) * 100,
                w: ((maxX - minX) / canvas.width) * 100,
                h: ((maxY - minY) / canvas.height) * 100,
            }
            : {cx: 50, cy: 50, w: 60, h: 60};

        return {canvas, bounds};
    }

    const maskReady = maskSrc
        ? loadImage(maskSrc, "anonymous").then(image => {
            const built = buildAlphaMask(image);
            maskAlphaCanvas = built.canvas;
            maskBounds = built.bounds;

            const maskUrl = `url(${built.canvas.toDataURL("image/png")})`;
            section.querySelectorAll(".pdpPersonalizeVisualDesign").forEach(el => {
                el.style.maskImage = maskUrl;
                el.style.webkitMaskImage = maskUrl;
            });
            section.querySelectorAll(".pdpPersonalizeVisualHitArea").forEach(el => {
                el.style.top = `${maskBounds.cy}%`;
                el.style.left = `${maskBounds.cx}%`;
                el.style.width = `${maskBounds.w}%`;
                el.style.height = `${maskBounds.h}%`;
            });
            updateDecalPreview();
        }).catch(() => {
            console.warn("Personalize: failed to load the mask image; using default bounds.");
        })
        : Promise.resolve();

    /************
     Current variant (options live in this section's own DOM; re-derived rather than
     trusting main-product-v1.js's private closure state)
     ************/
    function getCurrentVariantID() {
        if (productData.variants.length === 1) {
            return productData.variants[0].id;
        }
        const options = [];
        section.querySelectorAll(".productV1ContentOptionsItem").forEach(group => {
            const checked = group.querySelector("input:checked");
            if (checked) options.push(checked.value);
        });
        const match = productData.variants.find(v => JSON.stringify(v.options) === JSON.stringify(options));
        return (match || productData.variants[0]).id;
    }

    // the mirror variant's price already includes personalization, so there's no add-on
    // arithmetic - the base shown is the price of the variant that actually goes in the cart.
    // Both tabs cost the same, since one mirror covers Design and Engrave alike.
    // price() is the theme's own global cents-formatting helper (assets/theme-global.js),
    // already relied on by main-product-v1.js's own renderPrice(), so this stays visually
    // consistent with it
    document.addEventListener("discount:changed", function () {
        updatePersonalizePrice();
    });

    function updatePersonalizePrice() {
        if (!priceRealEl) return;
        const mirror = mirrorFor(getCurrentVariantID());
        if (!mirror) return;

        // Discount code on the session: priced through the same engine as the rest of the PDP,
        // but judged on the MIRROR product's own eligibility, not the page product's. The mirror
        // is a separate product and the codes are scoped to a collection, so the base grip being
        // covered says nothing about whether the personalized line will be.
        // pdp-personalize.liquid stamps the mirror's eligibility on .pdpPersonalizeFooterPrice,
        // which is nearer to priceRealEl than the section's own flag and so wins the lookup.
        const payable = window.OhSnapDiscount
            ? window.OhSnapDiscount.bestFor(mirror.price, 0, priceRealEl)
            : mirror.price;
        const anchor = Math.max(mirror.compareAt || 0, mirror.price);

        priceRealEl.textContent = price(payable, true);
        if (priceCompareEl) {
            // the amount goes beside the visually hidden "Regular price" label, not over it
            const compareText = anchor > payable ? price(anchor, true) : "";
            (priceCompareEl.querySelector("[data-compare-amount]") || priceCompareEl).textContent = compareText;
            priceCompareEl.hidden = !compareText;
        }
    }

    // show whichever visual block matches the active variant, show/hide the Personalize
    // button depending on whether that variant is enabled, and bail out of personalize
    // mode if the customer switches to a variant that isn't
    function syncActiveVariant() {
        const variantID = String(getCurrentVariantID());
        const matchingVisual = section.querySelector(`.pdpPersonalizeVisual[data-id="${variantID}"]`);
        section.querySelectorAll(".pdpPersonalizeVisual").forEach(v => v.classList.toggle("active", v === matchingVisual));

        // The map only contains variants that passed the full server-side gate - a mirror,
        // DoD's approval, and a mockup - so its mere presence is the authority here. The
        // matchingVisual check stays as a second condition because personalize mode needs a
        // block to render into, but it is no longer the only thing keeping an unapproved
        // variant out. mirror.available is the mirror's own stock, which is separate again.
        const mirror = mirrorFor(getCurrentVariantID());
        const personalizeAvailable = !!mirror && !!matchingVisual && !!mirror.available;

        toggleBtn?.classList.toggle("pdpPersonalizeToggleHidden", !personalizeAvailable);
        syncColorPicker();

        if (!personalizeAvailable && inner.classList.contains("isPersonalizing")) {
            exitPersonalize();
            return;
        }
        updateDecalPreview();
        updatePersonalizePrice();
    }

    section.querySelectorAll(".productV1ContentOptionsItemValuesLabelInput, .productV1ContentOptionsItemDropdownPopoverValueInput").forEach(input => {
        input.addEventListener("change", syncActiveVariant);
    });

    /************
     Color picker (pdp-personalize.liquid). Its radios drive the PDP's own color radios rather
     than being read directly, so price, mockup, gallery and URL all update through the one
     existing path: main-product-v1.js and syncActiveVariant() both listen for the PDP radio's
     change event, and syncColorPicker() mirrors the result back here.
     ************/
    const colorPicker = section.querySelector(".pdpPersonalizeColors");
    const colorInputs = colorPicker ? [...colorPicker.querySelectorAll(".pdpPersonalizeColorInput")] : [];

    function pdpColorInput(value) {
        return [...section.querySelectorAll(".productV1ContentOptionsItemColor .productV1ContentOptionsItemValuesLabelInput")]
            .find(input => input.value === value);
    }

    function syncColorPicker() {
        const current = section.querySelector(".productV1ContentOptionsItemColor .productV1ContentOptionsItemValuesLabelInput:checked");
        colorInputs.forEach(input => {
            input.checked = !!current && input.value === current.value;
        });
    }

    if (colorPicker) {
        // a color the PDP does not render (hidden_colors) cannot be selected through it, so it
        // is hidden here too - which also keeps the template test's arms honest
        colorInputs.forEach(input => {
            if (!pdpColorInput(input.value)) input.closest("label").hidden = true;
        });
        if (colorPicker.querySelectorAll(".pdpPersonalizeColor:not([hidden])").length < 2) {
            colorPicker.hidden = true;
        }

        colorInputs.forEach(input => {
            input.addEventListener("change", () => {
                const target = pdpColorInput(input.value);
                if (!target || target.checked) return;
                target.checked = true;
                target.dispatchEvent(new Event("change", {bubbles: true}));
            });
        });
        syncColorPicker();
    }

    /************
     Mode toggle - crossfades the outgoing content (shrink + fade out) against the
     incoming content (fade in while shrinking down from larger), clipped to the card so
     neither ever visibly pokes outside it, and animates the card's height to match.
     Cleanup is driven by the transform transitionend (not a setTimeout scheduled before
     the animation's requestAnimationFrame even fires) so it can't fire a frame early and
     cut the animation off - that mistiming was the "snaps into place at the end" bug.
     ************/
    const contentBox = section.querySelector(".productV1Content");
    const defaultContent = section.querySelector(".productV1ContentDefault");
    const CONTENT_TRANSITION_MS = 400;
    let pendingContentCleanup = null;

    function animateContentSwap(entering) {
        if (!contentBox || !defaultContent || !panel) return;

        // finish any transition already in progress immediately, rather than letting two overlap
        if (pendingContentCleanup) pendingContentCleanup();

        const fromEl = entering ? defaultContent : panel;
        const toEl = entering ? panel : defaultContent;

        // padding is read before the toggle below purely for the top/left inset - it's
        // unaffected by isPersonalizing, unlike border (see the comment on contentBoxExtra)
        const contentBoxStyle = getComputedStyle(contentBox);
        const padTop = parseFloat(contentBoxStyle.paddingTop) || 0;
        const padRight = parseFloat(contentBoxStyle.paddingRight) || 0;
        const padBottom = parseFloat(contentBoxStyle.paddingBottom) || 0;
        const padLeft = parseFloat(contentBoxStyle.paddingLeft) || 0;

        // measure the outgoing element's height *before* touching isPersonalizing - that
        // class is what the CSS uses to instantly hide/show these two, so toggling it first
        // would make fromEl display:none and measure as zero height
        const fromRawHeight = fromEl.getBoundingClientRect().height;

        inner.classList.toggle("isPersonalizing", entering);

        // isPersonalizing also thickens contentBox's own border (1px -> 2px, for the rainbow
        // gradient border), so contentBoxExtra/clientWidth have to be read *after* the toggle -
        // reading them before would inset fromEl/toEl by a couple pixels too much for the
        // border they're actually about to render inside, which for a tight-fitting title is
        // enough to wrap it onto an extra line for the whole transition
        const contentBoxExtra = padTop + padBottom
            + (parseFloat(contentBoxStyle.borderTopWidth) || 0) + (parseFloat(contentBoxStyle.borderBottomWidth) || 0);
        const startHeight = fromRawHeight + contentBoxExtra;

        // fromEl/toEl are plain children with no padding/border of their own, so their
        // width has to be set explicitly to contentBox's actual current content width
        // (clientWidth already excludes border) rather than derived from left+right insets -
        // the border-thickening above means the padding-box those insets are relative to
        // is itself 2px narrower than it was a moment ago.
        const contentWidth = contentBox.clientWidth - padLeft - padRight;

        // for a position:absolute element, the containing block is the padding BOX, so
        // top:0/left:0 lands at the inner edge of the border - before the padding even
        // starts - not inset by it the way static content is. Insetting explicitly by the
        // real padding is what makes the content actually keep its padding during the
        // transition instead of sitting flush against the border and snapping inward once
        // cleanup returns it to flow.
        fromEl.style.display = "block";
        fromEl.style.position = "absolute";
        fromEl.style.top = `${padTop}px`;
        fromEl.style.left = `${padLeft}px`;
        fromEl.style.right = "";
        fromEl.style.width = `${contentWidth}px`;

        toEl.style.display = "block";
        const endHeight = toEl.getBoundingClientRect().height + contentBoxExtra; // still static/in-flow here - its true final height

        toEl.style.position = "absolute";
        toEl.style.top = `${padTop}px`;
        toEl.style.left = `${padLeft}px`;
        toEl.style.right = "";
        toEl.style.width = `${contentWidth}px`;

        contentBox.classList.add("isContentTransitioning");
        contentBox.style.height = `${startHeight}px`;

        void contentBox.offsetHeight; // force layout so the styles above commit as the "from" state

        function cleanup() {
            toEl.removeEventListener("animationend", onAnimationEnd);
            clearTimeout(fallbackTimer);
            [defaultContent, panel].forEach(el => {
                el.style.position = "";
                el.style.top = "";
                el.style.left = "";
                el.style.right = "";
                el.style.width = "";
                el.style.display = "";
                el.classList.remove("pdpPersonalizeContentSwapOut", "pdpPersonalizeContentSwapIn");
            });
            contentBox.style.height = "";
            contentBox.classList.remove("isContentTransitioning");
            pendingContentCleanup = null;
        }

        function onAnimationEnd(e) {
            if (e.target === toEl && e.animationName === "pdpPersonalizeContentIn") cleanup();
        }

        toEl.addEventListener("animationend", onAnimationEnd);
        // safety net in case animationend never fires for some reason (e.g. reduced-motion
        // stripping animations elsewhere) - comfortably past the animation's own duration
        const fallbackTimer = setTimeout(cleanup, CONTENT_TRANSITION_MS + 150);
        pendingContentCleanup = cleanup;

        // opacity/transform run as CSS @keyframes animations (pdpPersonalizeContentOut/In,
        // defined in pdp-personalize.css) rather than a JS-driven transition - toEl just
        // flipped display:none -> block above, in the same tick as this class gets added,
        // and a transition set up that way is unreliable (see fadeIn's comment above for
        // the same issue); a keyframe animation plays from its defined 0% state regardless.
        fromEl.classList.add("pdpPersonalizeContentSwapOut");
        toEl.classList.add("pdpPersonalizeContentSwapIn");

        requestAnimationFrame(() => {
            contentBox.style.height = `${endHeight}px`;
        });
    }

    // simple opacity fade-in for whichever product-visual block (normal gallery or
    // personalize mockup) just got switched on via the isPersonalizing CSS toggle - that
    // switch is instant (display:none -> flex), so without this the reappearing side just
    // pops in with no transition at all. Uses a CSS @keyframes animation (pdpPersonalizeFadeIn,
    // defined in pdp-personalize.css) triggered by adding a class, rather than a JS-driven
    // transition - a transition set up via inline styles in the same tick as el's own
    // display:none -> flex flip is unreliable (Safari in particular can coalesce the two and
    // skip straight to the end state); a keyframe animation plays from its defined 0% state
    // regardless of when in the tick the element became visible.
    function fadeIn(el) {
        if (!el) return;
        el.classList.remove("pdpPersonalizeFadingIn");
        void el.offsetWidth; // restart the animation if this class was already present
        el.classList.add("pdpPersonalizeFadingIn");
        function cleanup() {
            el.removeEventListener("animationend", onAnimationEnd);
            clearTimeout(fallbackTimer);
            el.classList.remove("pdpPersonalizeFadingIn");
        }
        function onAnimationEnd(e) {
            if (e.target === el && e.animationName === "pdpPersonalizeFadeIn") cleanup();
        }
        el.addEventListener("animationend", onAnimationEnd);
        const fallbackTimer = setTimeout(cleanup, CONTENT_TRANSITION_MS + 150);
    }

    /************
     Mode toggle
     ************/
    function enterPersonalize() {
        if (toggleBtn?.classList.contains("pdpPersonalizeToggleHidden")) return;
        // product tabs and takeover CTAs can change the variant without a change event
        syncColorPicker();
        animateContentSwap(true);
        fadeIn(activeVisual());
    }

    function exitPersonalize() {
        animateContentSwap(false);
        resetState();
        fadeIn(section.querySelector(".productV1VisualVariant.active"));
        // the buy box is back: Shoplift gates in layout/theme.liquid re-measure whether their
        // element is on screen (they skip it while hidden behind the personalizer). Wait for the
        // swap to settle - mid-animation the buy box sits ~400px higher than where it lands
        setTimeout(() => {
            document.dispatchEvent(new CustomEvent("ohsnap:personalize-closed"));
        }, CONTENT_TRANSITION_MS + 150);
    }

    toggleBtn?.addEventListener("click", enterPersonalize);
    cancelBtn?.addEventListener("click", exitPersonalize);

    /************
     Tabs
     ************/
    section.querySelectorAll(".pdpPersonalizeTabsTab").forEach(tab => {
        tab.addEventListener("click", () => {
            activeTab = tab.dataset.tab;
            section.querySelectorAll(".pdpPersonalizeTabsTab").forEach(t => {
                t.classList.toggle("active", t === tab);
                t.setAttribute("aria-selected", t === tab ? "true" : "false");
            });
            section.querySelectorAll(".pdpPersonalizeTabPanel").forEach(p => {
                p.classList.toggle("active", p.dataset.panel === activeTab);
            });
            updateDecalPreview();
            updatePersonalizePrice();
        });
    });

    /************
     Pattern picker
     ************/
    function selectPatternSwatch(swatch) {
        section.querySelectorAll(".pdpPersonalizePatternSwatch.active").forEach(s => s.classList.remove("active"));
        swatch.classList.add("active");
        loadImage(swatch.dataset.fullSrc, "anonymous").then(img => {
            selectedPattern = {name: swatch.dataset.patternName, handle: swatch.dataset.patternHandle, image: img};
            updateDecalPreview();
        });
    }

    section.querySelectorAll(".pdpPersonalizePatternSwatch").forEach(swatch => {
        swatch.addEventListener("click", () => {
            if (patternPicker?.classList.contains("isDisabled")) return;
            selectPatternSwatch(swatch);
        });
    });

    // pattern name tooltip - the same bubble as the PDP's color swatch tooltip
    // (.productV1ContentOptionsItemValuesLabelTooltip), but ONE floating element positioned
    // over the swatch rather than one inside each: the picker is a scrolling box with
    // overflow clipping, which would cut off the top row's tooltip. Hover shows it on
    // desktop; touch has no hover, so a tap flashes it for a second, as the PDP does.
    const patternTooltip = document.createElement("div");
    patternTooltip.className = "pdpPersonalizePatternTooltip";
    patternTooltip.setAttribute("aria-hidden", "true");
    document.body.appendChild(patternTooltip);
    let patternTooltipTimer = null;

    function showPatternTooltip(swatch) {
        const rect = swatch.getBoundingClientRect();
        patternTooltip.textContent = swatch.dataset.patternName;
        patternTooltip.style.left = `${rect.left + rect.width / 2}px`;
        patternTooltip.style.top = `${rect.top}px`;
        patternTooltip.classList.add("active");
    }

    function hidePatternTooltip() {
        clearTimeout(patternTooltipTimer);
        patternTooltip.classList.remove("active");
    }

    const canHover = window.matchMedia("(hover: hover)").matches;
    section.querySelectorAll(".pdpPersonalizePatternSwatch").forEach(swatch => {
        if (canHover) {
            swatch.addEventListener("mouseenter", () => showPatternTooltip(swatch));
            swatch.addEventListener("mouseleave", hidePatternTooltip);
        } else {
            swatch.addEventListener("click", () => {
                if (patternPicker?.classList.contains("isDisabled")) return;
                showPatternTooltip(swatch);
                clearTimeout(patternTooltipTimer);
                patternTooltipTimer = setTimeout(hidePatternTooltip, 1000);
            });
        }
        swatch.addEventListener("focus", () => {
            if (swatch.matches(":focus-visible")) showPatternTooltip(swatch);
        });
        swatch.addEventListener("blur", hidePatternTooltip);
    });

    // it is placed once, in viewport coordinates, so any scroll would leave it behind
    section.querySelector(".pdpPersonalizePatternPickerScroll")?.addEventListener("scroll", hidePatternTooltip, {passive: true});
    window.addEventListener("scroll", hidePatternTooltip, {passive: true});

    // preload whichever swatch renders as the default active one
    const initialSwatch = section.querySelector(".pdpPersonalizePatternSwatch.active");
    if (initialSwatch) {
        selectPatternSwatch(initialSwatch);
    }

    /************
     Upload + crop (pan / scale)
     ************/
    // the design's on-screen box, derived from maskBounds against the always-visible stack
    // rather than the hit area's own rect - the hit area is display:none except while a
    // photo is uploaded, so measuring it directly would read back a zero-sized rect at
    // exactly the moments (initial cover-scale calc, pattern/engrave export) that matter
    function maskAreaRect(stackRect) {
        const rect = stackRect || activeVisual()?.querySelector(".pdpPersonalizeVisualStack")?.getBoundingClientRect();
        if (!rect) return {left: 0, top: 0, width: 0, height: 0};
        const width = (maskBounds.w / 100) * rect.width;
        const height = (maskBounds.h / 100) * rect.height;
        return {
            left: rect.left + (maskBounds.cx / 100) * rect.width - width / 2,
            top: rect.top + (maskBounds.cy / 100) * rect.height - height / 2,
            width,
            height,
        };
    }

    // Larger than the print area, the photo pans until its edge meets the area's edge; smaller,
    // it moves freely inside the area. Either way no part of it can leave the area entirely.
    function maxCropOffset(scale) {
        const rect = maskAreaRect();
        return {
            x: Math.abs(uploadedPhoto.baseWidth * scale - rect.width) / 2,
            y: Math.abs(uploadedPhoto.baseHeight * scale - rect.height) / 2,
        };
    }

    // scale 1 is "cover" (the photo fills the print area). The floor is "contain" - the whole
    // photo visible inside the area - so a customer can shrink a photo to show all of it; the
    // uncovered part of the area is transparent in the print file and prints no ink, exactly
    // as the preview shows (the mockup's own surface). 4x is the ceiling it always had.
    const MAX_CROP_SCALE = 4;

    function minCropScale() {
        const rect = maskAreaRect();
        if (!uploadedPhoto || !rect.width || !rect.height) return 1;
        return Math.min(1, rect.width / uploadedPhoto.baseWidth, rect.height / uploadedPhoto.baseHeight);
    }

    function setCropScale(scale) {
        cropState.scale = Math.max(minCropScale(), Math.min(MAX_CROP_SCALE, scale));
        const max = maxCropOffset(cropState.scale);
        cropState.x = Math.max(-max.x, Math.min(max.x, cropState.x));
        cropState.y = Math.max(-max.y, Math.min(max.y, cropState.y));
        applyCropTransform();
        // zooming in spreads fewer source pixels over the print area
        updateQualityWarning();
    }

    function applyCropTransform() {
        const decalArt = activeDecalArt();
        if (!uploadedPhoto || !decalArt) return;
        decalArt.style.top = `${maskBounds.cy}%`;
        decalArt.style.left = `${maskBounds.cx}%`;
        decalArt.style.width = `${uploadedPhoto.baseWidth}px`;
        decalArt.style.height = `${uploadedPhoto.baseHeight}px`;
        decalArt.style.objectFit = "fill";
        decalArt.style.transform = `translate(-50%, -50%) translate(${cropState.x}px, ${cropState.y}px) scale(${cropState.scale})`;
    }

    let fadeTimer = null;

    function resetFadeTimer() {
        section.querySelectorAll(".pdpPersonalizeVisual").forEach(v => v.classList.remove("isFaded"));
        clearTimeout(fadeTimer);
        fadeTimer = setTimeout(() => {
            section.querySelectorAll(".pdpPersonalizeVisual").forEach(v => v.classList.add("isFaded"));
        }, 2000);
    }

    uploadButton?.addEventListener("click", () => uploadInput.click());

    // iPhone photos are often HDR (a gain map on top of the SDR image). An <img> renders that
    // map on an HDR screen, so the preview glows far brighter than anything a canvas - and so the
    // print file and the cart mockup - can reproduce. Redrawing the photo through a canvas
    // flattens it to the SDR image the export will actually use, so the customer approves what
    // gets printed. It also caps the size: a 12MP photo is 10x what the print (450px) or the
    // preview needs, and every drag re-composites it.
    const UPLOAD_MAX_EDGE = 2400;

    async function standardDynamicRange(file) {
        const source = await loadImage(URL.createObjectURL(file));
        const k = Math.min(1, UPLOAD_MAX_EDGE / Math.max(source.naturalWidth, source.naturalHeight));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(source.naturalWidth * k);
        canvas.height = Math.round(source.naturalHeight * k);
        canvas.getContext("2d", {colorSpace: "srgb"}).drawImage(source, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(source.src);
        // PNG keeps a transparent upload transparent; everything else is a photo
        const type = file.type === "image/png" ? "image/png" : "image/jpeg";
        const blob = await new Promise(resolve => canvas.toBlob(resolve, type, 0.92));
        return blob ? loadImage(URL.createObjectURL(blob)) : loadImage(URL.createObjectURL(file));
    }

    /************
     Photo quality check. A photo that arrives heavily compressed or too small prints blocky or
     soft, and the Personalization Terms promise a warning before we print one as-is. Three
     signals, any one of which shows "Low quality photo detected" under the preview caption
     and records _quality_warning on the line item for the print team:

       - the JPEG's own quality setting, read from its luma quantization table - exact and
         free for a JPEG straight from its source;
       - blockiness: how much sharper the pixel jumps are on JPEG's 8x8 block seams than
         elsewhere. Catches what the header cannot - a compressed photo screenshotted, or
         re-saved at a "high" quality - and survives cropping, since every grid offset is
         tried. A photo compressed and THEN resized smears the grid and reads as clean, which
         is what the resolution check backstops;
       - resolution: the source pixels that end up across the print area at the current
         zoom, against the artboard DoD prints at. Re-checked whenever the photo is resized.

     Both compression checks run on the file exactly as uploaded: the SDR flatten below
     resamples, which would erase the very block grid being measured.
     ************/
    const QUALITY_MIN_JPEG = 60;          // IJG quality; phones and messaging apps save 80-95
    const QUALITY_MAX_BLOCKINESS = 1.4;   // 1.0 is no grid; clean photos measure 1.00-1.03
    const QUALITY_MIN_COVERAGE = 0.75;    // of the artboard's width, i.e. ~225 DPI at DoD's 300

    // standard IJG luminance table - the reference its quality scale is defined against
    const IJG_LUMA_SUM = [16, 11, 10, 16, 24, 40, 51, 61, 12, 12, 14, 19, 26, 58, 60, 55, 14, 13, 16, 24, 40, 57, 69, 56,
        14, 17, 22, 29, 51, 87, 80, 62, 18, 22, 37, 56, 68, 109, 103, 77, 24, 35, 55, 64, 81, 104, 113, 92,
        49, 64, 78, 87, 103, 121, 120, 101, 72, 92, 95, 98, 112, 100, 103, 99].reduce((a, b) => a + b, 0);

    // the first quantization table of a JPEG, as an IJG quality 1-100; null if not a JPEG
    function jpegQuality(bytes) {
        if (bytes[0] !== 0xFF || bytes[1] !== 0xD8) return null;
        let i = 2;
        while (i + 4 < bytes.length) {
            if (bytes[i] !== 0xFF) { i++; continue; }
            const marker = bytes[i + 1];
            const length = (bytes[i + 2] << 8) | bytes[i + 3];
            if (marker === 0xDB) {
                const sixteenBit = bytes[i + 4] >> 4;
                let sum = 0;
                for (let k = 0; k < 64; k++) {
                    sum += sixteenBit ? (bytes[i + 5 + 2 * k] << 8) | bytes[i + 6 + 2 * k] : bytes[i + 5 + k];
                }
                const scale = (sum * 100) / IJG_LUMA_SUM;
                return Math.max(1, Math.min(100, Math.round(scale <= 100 ? (200 - scale) / 2 : 5000 / scale)));
            }
            if (marker === 0xDA || marker === 0xD9) return null;
            i += 2 + length;
        }
        return null;
    }

    // seam-to-elsewhere ratio of luma jumps, best over all 8 grid offsets, on a 1:1 centre crop
    function blockiness(image) {
        const w = Math.min(1024, image.naturalWidth);
        const h = Math.min(1024, image.naturalHeight);
        if (w < 32 || h < 32) return 1;
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d", {willReadFrequently: true});
        ctx.drawImage(image, Math.floor((image.naturalWidth - w) / 2), Math.floor((image.naturalHeight - h) / 2), w, h, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, w, h).data;
        const luma = new Float32Array(w * h);
        for (let p = 0, q = 0; p < luma.length; p++, q += 4) {
            luma[p] = 0.299 * data[q] + 0.587 * data[q + 1] + 0.114 * data[q + 2];
        }
        const col = new Float64Array(8), colN = new Float64Array(8), row = new Float64Array(8), rowN = new Float64Array(8);
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                const v = luma[y * w + x];
                if (x > 0) { col[(x - 1) % 8] += Math.abs(v - luma[y * w + x - 1]); colN[(x - 1) % 8]++; }
                if (y > 0) { row[(y - 1) % 8] += Math.abs(v - luma[(y - 1) * w + x]); rowN[(y - 1) % 8]++; }
            }
        }
        const total = col.reduce((a, b) => a + b) + row.reduce((a, b) => a + b);
        const totalN = colN.reduce((a, b) => a + b) + rowN.reduce((a, b) => a + b);
        let best = 1;
        for (let ox = 0; ox < 8; ox++) {
            for (let oy = 0; oy < 8; oy++) {
                const on = (col[ox] + row[oy]) / (colN[ox] + rowN[oy]);
                const off = (total - col[ox] - row[oy]) / (totalN - colN[ox] - rowN[oy]);
                if (off > 0) best = Math.max(best, on / off);
            }
        }
        return best;
    }

    async function analyzePhotoQuality(file) {
        const result = {jpegQuality: null, blockiness: 1};
        try {
            result.jpegQuality = jpegQuality(new Uint8Array(await file.slice(0, 256 * 1024).arrayBuffer()));
        } catch (e) { /* unreadable header - leave it to the pixel check */ }
        try {
            const image = await loadImage(URL.createObjectURL(file));
            result.blockiness = blockiness(image);
            URL.revokeObjectURL(image.src);
        } catch (e) { /* undecodable - the upload itself will fail visibly */ }
        result.compressed = (result.jpegQuality !== null && result.jpegQuality < QUALITY_MIN_JPEG)
            || result.blockiness > QUALITY_MAX_BLOCKINESS;
        return result;
    }

    // source pixels across the print area's width at the current zoom, vs the artboard's width
    function photoCoverage() {
        if (!uploadedPhoto) return 1;
        const mirror = mirrorFor(getCurrentVariantID());
        const artboard = (mirror && mirror.artboardW) || 450;
        const rect = maskAreaRect();
        const sourcePx = rect.width * (uploadedPhoto.image.naturalWidth / (uploadedPhoto.baseWidth * cropState.scale));
        return sourcePx / artboard;
    }

    function photoQualityIssues() {
        if (!uploadedPhoto) return [];
        const q = uploadedPhoto.quality || {};
        const issues = [];
        if (q.jpegQuality !== null && q.jpegQuality !== undefined && q.jpegQuality < QUALITY_MIN_JPEG) issues.push(`jpeg-quality-${q.jpegQuality}`);
        if (q.blockiness > QUALITY_MAX_BLOCKINESS) issues.push(`blocky-${q.blockiness.toFixed(2)}`);
        const coverage = photoCoverage();
        if (coverage < QUALITY_MIN_COVERAGE) issues.push(`low-resolution-${Math.round(coverage * 100)}pct`);
        return issues;
    }

    function updateQualityWarning() {
        const show = activeTab !== "engrave" && photoQualityIssues().length > 0;
        const visual = activeVisual();
        section.querySelectorAll(".pdpPersonalizeVisualQuality").forEach(el => {
            el.hidden = !(show && el.closest(".pdpPersonalizeVisual") === visual);
        });
    }

    uploadInput?.addEventListener("change", () => {
        const file = uploadInput.files[0];
        if (!file) return;
        const quality = analyzePhotoQuality(file);
        standardDynamicRange(file).catch(() => loadImage(URL.createObjectURL(file))).then(img => {
            const rect = maskAreaRect();
            const coverScale = Math.max(rect.width / img.naturalWidth, rect.height / img.naturalHeight);
            uploadedPhoto = {
                file,
                image: img,
                baseWidth: img.naturalWidth * coverScale,
                baseHeight: img.naturalHeight * coverScale,
            };
            // attach the verdict when it lands, unless another photo has replaced this one
            quality.then(result => {
                if (uploadedPhoto && uploadedPhoto.file === file) {
                    uploadedPhoto.quality = result;
                    updateQualityWarning();
                }
            });
            cropState = {x: 0, y: 0, scale: 1};

            section.querySelectorAll(".pdpPersonalizePatternSwatch.active").forEach(s => s.classList.remove("active"));
            selectedPattern = null;
            patternPicker?.classList.add("isDisabled");

            uploadButton.hidden = true;
            uploadChip.hidden = false;
            uploadChipName.textContent = file.name;
            section.querySelectorAll(".pdpPersonalizeVisual").forEach(v => v.classList.add("hasUpload"));

            updateDecalPreview();
            resetFadeTimer();

            // on a phone the upload button sits well below the preview, so the photo and its
            // handles would appear off-screen - bring the preview into view
            const visual = activeVisual();
            if (visual && visual.getBoundingClientRect().bottom < 80) {
                visual.scrollIntoView({behavior: "smooth", block: "start"});
            }
        });
    });

    uploadChip?.addEventListener("click", () => {
        uploadedPhoto = null;
        uploadInput.value = "";
        patternPicker?.classList.remove("isDisabled");
        uploadButton.hidden = false;
        uploadChip.hidden = true;
        section.querySelectorAll(".pdpPersonalizeVisual").forEach(v => v.classList.remove("hasUpload", "isFaded"));
        updateDecalPreview();
    });

    // pan/resize are delegated at the section level (not bound to one fixed element) since
    // only one .pdpPersonalizeVisual block is visible/interactive at a time, and the hit
    // area itself isn't clipped by overflow, so its corner handles stay clickable
    let dragState = null;
    let resizeState = null;
    // two-finger pinch on the photo: both active touches, and the spread/scale it started at
    const pinchPointers = new Map();
    let pinchState = null;

    section.addEventListener("pointerdown", e => {
        if (!uploadedPhoto) return;

        if (e.pointerType === "touch" && e.target.closest(".pdpPersonalizeVisualHitArea")) {
            pinchPointers.set(e.pointerId, {x: e.clientX, y: e.clientY});
            if (pinchPointers.size === 2) {
                const [a, b] = [...pinchPointers.values()];
                pinchState = {startDist: Math.hypot(a.x - b.x, a.y - b.y) || 1, startScale: cropState.scale};
                dragState = null;
                resizeState = null;
                activeHitArea()?.classList.remove("isDragging");
                resetFadeTimer();
                return;
            }
        }

        const handle = e.target.closest(".pdpPersonalizeVisualDecalHandle");
        if (handle) {
            e.stopPropagation();
            handle.setPointerCapture(e.pointerId);
            const rect = maskAreaRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;
            resizeState = {pointerId: e.pointerId, cx, cy, startDist: Math.hypot(e.clientX - cx, e.clientY - cy), startScale: cropState.scale};
            resetFadeTimer();
            return;
        }

        const hitArea = e.target.closest(".pdpPersonalizeVisualHitArea");
        if (!hitArea) return;
        hitArea.setPointerCapture(e.pointerId);
        hitArea.classList.add("isDragging");
        dragState = {pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, startCropX: cropState.x, startCropY: cropState.y};
        resetFadeTimer();
    });

    section.addEventListener("pointermove", e => {
        if (pinchPointers.has(e.pointerId)) {
            pinchPointers.set(e.pointerId, {x: e.clientX, y: e.clientY});
            if (pinchState && pinchPointers.size === 2) {
                resetFadeTimer();
                const [a, b] = [...pinchPointers.values()];
                setCropScale(pinchState.startScale * (Math.hypot(a.x - b.x, a.y - b.y) / pinchState.startDist));
                return;
            }
        }

        if (dragState && dragState.pointerId === e.pointerId) {
            resetFadeTimer();
            const dx = e.clientX - dragState.startX;
            const dy = e.clientY - dragState.startY;
            const max = maxCropOffset(cropState.scale);
            cropState.x = Math.max(-max.x, Math.min(max.x, dragState.startCropX + dx));
            cropState.y = Math.max(-max.y, Math.min(max.y, dragState.startCropY + dy));
            applyCropTransform();
            return;
        }

        if (resizeState && resizeState.pointerId === e.pointerId) {
            resetFadeTimer();
            const dist = Math.hypot(e.clientX - resizeState.cx, e.clientY - resizeState.cy);
            setCropScale(resizeState.startScale * (dist / resizeState.startDist));
        }
    });

    // desktop: the mouse wheel and a trackpad pinch (which browsers report as a wheel event
    // with ctrlKey set) resize the photo while the pointer is over it. Not passive, so the
    // page neither scrolls nor zooms instead.
    section.addEventListener("wheel", e => {
        if (!uploadedPhoto || !e.target.closest(".pdpPersonalizeVisualHitArea")) return;
        e.preventDefault();
        resetFadeTimer();
        const sensitivity = e.ctrlKey ? 0.01 : 0.001;
        setCropScale(cropState.scale * Math.exp(-e.deltaY * sensitivity));
    }, {passive: false});

    function endInteraction(e) {
        pinchPointers.delete(e.pointerId);
        if (pinchPointers.size < 2) pinchState = null;
        if (dragState && dragState.pointerId === e.pointerId) {
            activeHitArea()?.classList.remove("isDragging");
            dragState = null;
        }
        if (resizeState && resizeState.pointerId === e.pointerId) {
            resizeState = null;
        }
    }

    section.addEventListener("pointerup", endInteraction);
    section.addEventListener("pointercancel", endInteraction);

    /************
     Engrave tab
     ************/
    function updateEngraveCounter() {
        if (!engraveCounter || !engraveInput) return;
        engraveCounter.textContent = `${engraveInput.value.length}/${engraveInput.maxLength}`;
    }

    // Engraving takes English letters, digits and spaces only. Accents are dropped from accented
    // letters; anything else (emoji, symbols, other scripts) is removed as it is typed, pasted or
    // autocorrected in, and the hint
    // under the field flashes red so the customer sees why a character didn't stick. Applied
    // again at submit in case the value arrived some other way.
    const ENGRAVE_DISALLOWED = /[^A-Za-z0-9 ]/g;
    const engraveHint = section.querySelector(".pdpPersonalizeEngraveHint");
    let engraveHintTimer = null;

    // accented Latin letters become their plain letter (José -> Jose, Zoë -> Zoe) rather than
    // vanishing; everything else outside A-Z, 0-9 and space is dropped. NFD splits "é" into "e" +
    // a combining accent, so stripping U+0300-036F leaves the base letter, one char for one char.
    function engraveSafe(text) {
        return (text || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(ENGRAVE_DISALLOWED, "");
    }

    function sanitizeEngraveInput() {
        const value = engraveInput.value;
        const clean = engraveSafe(value);
        if (clean === value) return;
        // keep the caret where the customer was typing, minus whatever was removed before it
        const caret = engraveInput.selectionStart ?? value.length;
        const removedBefore = value.slice(0, caret).length - engraveSafe(value.slice(0, caret)).length;
        engraveInput.value = clean;
        try {
            engraveInput.setSelectionRange(caret - removedBefore, caret - removedBefore);
        } catch (e) { /* some input types don't support selection; harmless */ }
        if (engraveHint) {
            engraveHint.classList.add("isWarning");
            clearTimeout(engraveHintTimer);
            engraveHintTimer = setTimeout(() => engraveHint.classList.remove("isWarning"), 2500);
        }
    }

    engraveInput?.addEventListener("input", e => {
        // mid-composition (IME, some mobile keyboards) the text isn't final; clean it on compositionend
        if (!e.isComposing) sanitizeEngraveInput();
        engravingText = engraveInput.value;
        updateDecalPreview();
        updateEngraveCounter();
    });
    engraveInput?.addEventListener("compositionend", () => {
        sanitizeEngraveInput();
        engravingText = engraveInput.value;
        updateDecalPreview();
        updateEngraveCounter();
    });

    section.querySelectorAll(".pdpPersonalizeFontSwatch").forEach(swatch => {
        swatch.addEventListener("click", () => {
            section.querySelectorAll(".pdpPersonalizeFontSwatch.active").forEach(s => s.classList.remove("active"));
            swatch.classList.add("active");
            selectedFont = {family: swatch.dataset.fontFamily, label: swatch.dataset.fontLabel, weight: parseInt(swatch.dataset.fontWeight, 10) || 400, file: swatch.dataset.fontFile};
            updateDecalPreview();
            // the font may not have finished downloading/decoding yet the first time it's picked,
            // which would measure (and therefore size) against a fallback font instead of the real
            // one; re-fit once it's actually ready so the size doesn't jump or measure wrong
            document.fonts.load(`${selectedFont.weight} 16px ${selectedFont.family}`).then(updateDecalPreview);
        });
    });

    /************
     Live decal preview (design pattern / uploaded photo / engraving text)
     ************/
    function updateDecalPreview() {
        const decalArt = activeDecalArt();
        const decalText = activeDecalText();
        if (!decalArt || !decalText) return;

        // the highlight/shadow overlay only adds depth to a pattern/photo decal - it has
        // no business sitting on top of engraved text, nor on the bare mockup before any
        // design has been applied (including while the default pattern is still loading)
        const overlay = activeOverlay();
        overlay?.classList.toggle("active", activeTab !== "engrave" && Boolean(uploadedPhoto || selectedPattern));

        const caption = activeVisual()?.querySelector(".pdpPersonalizeVisualCaption");
        if (caption) {
            if (activeTab === "engrave") {
                caption.style.display = "none";
            } else {
                caption.style.display = "block";
                caption.textContent = uploadedPhoto ? "Drag & resize photo" : "Pick a really cool design";
            }
        }
        updateQualityWarning();

        if (activeTab === "engrave") {
            decalArt.classList.remove("active");
            decalText.classList.add("active");
            decalText.textContent = engravingText;
            decalText.style.fontFamily = selectedFont.family;
            decalText.style.fontWeight = selectedFont.weight;
            decalText.style.top = `${maskBounds.cy}%`;
            decalText.style.left = `${maskBounds.cx}%`;
            decalText.style.width = `${maskBounds.w}%`;
            decalText.style.height = `${maskBounds.h}%`;
            // transform is finished below, once the fitted size is known - the nudge that
            // centres the ink depends on the font size

            // size the live preview with the exact same fit used for the exports, so the
            // on-page text and the cart thumbnail never drift apart
            const rect = maskAreaRect();
            const maxWidth = rect.width * (engraveMaxWidthPct / 100);
            const maxHeight = rect.height * (engraveMaxHeightPct / 100);
            const fontSize = engravingText ? fitEngravingFontSize(engravingText, maxWidth, maxHeight, selectedFont.family, selectedFont.weight) : maxHeight;
            decalText.style.fontSize = `${fontSize}px`;

            // flex centring + line-height:1 puts the LINE BOX centre on the anchor; shift so the
            // ink's centre lands there instead, matching both exports exactly
            const domInk = inkMetrics(engravingText, fontSize, selectedFont.family, selectedFont.weight);
            const nudge = -(domInk.inkFromBaseline + domInk.baselineFromLineCentre);
            decalText.style.transform = `translate(-50%, -50%) translateY(${nudge}px)`;
            return;
        }

        decalText.classList.remove("active");

        if (uploadedPhoto) {
            decalArt.src = uploadedPhoto.image.src;
            decalArt.classList.add("active");
            applyCropTransform();
        } else if (selectedPattern) {
            decalArt.src = selectedPattern.image.src;
            decalArt.style.top = `${maskBounds.cy}%`;
            decalArt.style.left = `${maskBounds.cx}%`;
            decalArt.style.width = `${maskBounds.w}%`;
            decalArt.style.height = `${maskBounds.h}%`;
            decalArt.style.objectFit = "cover";
            decalArt.style.transform = "translate(-50%, -50%)";
            decalArt.classList.add("active");
        } else {
            decalArt.classList.remove("active");
        }
    }

    /************
     Reset (Cancel)
     ************/
    function resetState() {
        activeTab = "design";
        engravingText = "";
        selectedFont = defaultFont;
        uploadedPhoto = null;
        cropState = {x: 0, y: 0, scale: 1};

        if (engraveInput) engraveInput.value = "";
        updateEngraveCounter();
        if (uploadInput) uploadInput.value = "";
        if (uploadButton) uploadButton.hidden = false;
        if (uploadChip) uploadChip.hidden = true;
        patternPicker?.classList.remove("isDisabled");
        section.querySelectorAll(".pdpPersonalizeVisual").forEach(v => v.classList.remove("hasUpload", "isFaded"));

        section.querySelectorAll(".pdpPersonalizeTabsTab").forEach(t => {
            t.classList.toggle("active", t.dataset.tab === "design");
            t.setAttribute("aria-selected", t.dataset.tab === "design" ? "true" : "false");
        });
        section.querySelectorAll(".pdpPersonalizeTabPanel").forEach(p => {
            p.classList.toggle("active", p.dataset.panel === "design");
        });
        section.querySelectorAll(".pdpPersonalizeFontSwatch").forEach(f => {
            f.classList.toggle("active", f.dataset.fontFamily === defaultFont.family);
        });

        const first = section.querySelector(".pdpPersonalizePatternGroupGrid .pdpPersonalizePatternSwatch");
        if (first) {
            selectPatternSwatch(first);
        } else {
            selectedPattern = null;
            updateDecalPreview();
        }
        updatePersonalizePrice();
    }

    /************
     Canvas export - print file (isolated artwork, clipped to the true mask shape,
     cropped to the mask's own bounding box so resolution isn't spent on empty margin)
     ************/
    function drawCoverAt(ctx, img, x, y, w, h) {
        const scale = Math.max(w / img.naturalWidth, h / img.naturalHeight);
        const dw = img.naturalWidth * scale;
        const dh = img.naturalHeight * scale;
        ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
    }

    // Digital on Demand drops our file into a fixed artboard per product, so the export has to
    // come out at exactly the pixel dimensions they specify (custom.print_artboard_width /
    // _height on the mirror product) rather than whatever the mask happens to be. The design is
    // fitted into that artboard preserving the print shape's own aspect ratio and centred, with
    // the remainder left transparent - never stretched to fill, which would distort the artwork
    // on any product whose artboard isn't the same shape as its print area. Artboards aren't all
    // square; the wallet's is taller than it is wide.
    //
    // With no artboard set we fall back to the old behaviour, a 2000px longest edge.
    // Shared by the raster export and the engraving SVG so the two can never disagree about
    // where the print area sits inside the artboard. Returns everything in artboard pixels.
    function printGeometry(mirror) {
        const maskW = maskAlphaCanvas.width;
        const maskH = maskAlphaCanvas.height;
        const bx = (maskBounds.cx / 100) * maskW - (maskBounds.w / 100) * maskW / 2;
        const by = (maskBounds.cy / 100) * maskH - (maskBounds.h / 100) * maskH / 2;
        const bw = (maskBounds.w / 100) * maskW;
        const bh = (maskBounds.h / 100) * maskH;

        const artW = mirror?.artboardW > 0 ? mirror.artboardW : 0;
        const artH = mirror?.artboardH > 0 ? mirror.artboardH : 0;

        if (artW && artH) {
            const fit = Math.min(artW / bw, artH / bh);
            const printW = Math.max(1, Math.round(bw * fit));
            const printH = Math.max(1, Math.round(bh * fit));
            return {
                bx, by, bw, bh,
                canvasW: artW, canvasH: artH, printW, printH,
                offX: Math.round((artW - printW) / 2),
                offY: Math.round((artH - printH) / 2),
            };
        }

        const scale = PRINT_SIZE / Math.max(bw, bh);
        const printW = Math.max(1, Math.round(bw * scale));
        const printH = Math.max(1, Math.round(bh * scale));
        return {bx, by, bw, bh, canvasW: printW, canvasH: printH, printW, printH, offX: 0, offY: 0};
    }

    /************
     Engraving vector export

     DoD engraves by following vector paths, so the engraving print file is an SVG rather
     than a raster - the laser traces the outline instead of dithering pixels. The glyphs are
     converted to <path> data rather than shipped as <text>, because DoD does not have our
     fonts installed and a <text> element would silently render in whatever their software
     substitutes. That conversion needs the font's real outlines, which is why every engraving
     font is a TTF/OTF in assets/ (see the note in pdp-personalize.liquid).

     opentype.js is ~240KB, so it is fetched on demand the first time an engraving is
     exported rather than shipped with the PDP.
     ************/
    let opentypePromise = null;

    function loadOpentype() {
        if (opentypePromise) return opentypePromise;
        opentypePromise = new Promise((resolve, reject) => {
            if (window.opentype) return resolve(window.opentype);
            const src = panel.dataset.opentypeSrc;
            if (!src) return reject(new Error("opentype.js asset URL is missing"));
            const script = document.createElement("script");
            script.src = src;
            script.onload = () => window.opentype
                ? resolve(window.opentype)
                : reject(new Error("opentype.js loaded but window.opentype is undefined"));
            script.onerror = () => reject(new Error("opentype.js failed to load"));
            document.head.appendChild(script);
        });
        return opentypePromise;
    }

    const outlineFontCache = new Map();

    function loadOutlineFont(url) {
        if (outlineFontCache.has(url)) return outlineFontCache.get(url);
        const pending = (async () => {
            const ot = await loadOpentype();
            const response = await fetch(url);
            if (!response.ok) throw new Error(`outline font fetch failed (${response.status})`);
            return ot.parse(await response.arrayBuffer());
        })();
        outlineFontCache.set(url, pending);
        return pending;
    }

    // Placement mirrors the canvas path exactly, from the same two shared helpers:
    // fitEngravingFontSize for the size and inkMetrics for the vertical offset. Both the
    // preview and the print file therefore centre the string's ink in the print area, so what
    // the customer approves is what gets engraved.
    // SVG path data with every contour explicitly closed. opentype.js's own toPathData() is not
    // safe for a vector print file: by default it "optimizes" away the final line segment of the
    // whole path, expecting a Z to replace it, but this build's glyph outlines carry no Z at all.
    // The fill still renders (SVG closes subpaths implicitly when filling), so the preview and any
    // browser view look right - but DoD's engraver follows outlines as paths, and the last edge of
    // the last letter was simply missing (the inside of the P in "CSP", order #1585163). Its
    // options form keeps the edge but flips Y. So the commands are serialized here, each contour
    // ending in Z, which reproduces the glyph exactly whether or not the closing edge is explicit.
    function closedPathData(path, decimals) {
        const n = v => String(+v.toFixed(decimals));
        let d = "";
        let open = false;
        path.commands.forEach(c => {
            if (c.type === "M") {
                if (open) d += "Z";
                d += `M${n(c.x)} ${n(c.y)}`;
                open = true;
            } else if (c.type === "L") {
                d += `L${n(c.x)} ${n(c.y)}`;
            } else if (c.type === "Q") {
                d += `Q${n(c.x1)} ${n(c.y1)} ${n(c.x)} ${n(c.y)}`;
            } else if (c.type === "C") {
                d += `C${n(c.x1)} ${n(c.y1)} ${n(c.x2)} ${n(c.y2)} ${n(c.x)} ${n(c.y)}`;
            } else if (c.type === "Z") {
                d += "Z";
                open = false;
            }
        });
        if (open) d += "Z";
        return d;
    }

    function buildEngraveSvg(otFont, text, font, geo) {
        const fontSize = fitEngravingFontSize(
            text,
            geo.printW * (engraveMaxWidthPct / 100),
            geo.printH * (engraveMaxHeightPct / 100),
            font.family,
            font.weight,
        );

        const centerX = geo.offX + geo.printW / 2;
        const centerY = geo.offY + geo.printH / 2;
        const advance = otFont.getAdvanceWidth(text, fontSize);
        const ink = inkMetrics(text, fontSize, font.family, font.weight);
        const baselineY = centerY - ink.inkFromBaseline;

        const path = otFont.getPath(text, centerX - advance / 2, baselineY, fontSize);
        const d = closedPathData(path, 3);
        if (!d) throw new Error("outlined engraving came out empty");

        // width/height match the artboard 1:1 in user units, so DoD's fixed artboard takes it
        // without rescaling; viewBox makes that mapping explicit.
        return [
            `<svg xmlns="http://www.w3.org/2000/svg" width="${geo.canvasW}" height="${geo.canvasH}"`,
            ` viewBox="0 0 ${geo.canvasW} ${geo.canvasH}">`,
            `<path d="${d}" fill="#000000"/>`,
            `</svg>`,
        ].join("");
    }

    function renderPrintCanvas(mirror) {
        const {bx, by, bw, bh, canvasW, canvasH, printW, printH, offX, offY} = printGeometry(mirror);

        const canvas = document.createElement("canvas");
        canvas.width = canvasW;
        canvas.height = canvasH;
        const ctx = canvas.getContext("2d");
        // everything below is authored in print-area coordinates; the translate is what places
        // that area inside the artboard, so no other maths in here needs to know about padding
        ctx.translate(offX, offY);

        if (activeTab === "engrave") {
            // isolated engraving artwork only - no product background, matching the pattern/photo print files
            ctx.fillStyle = "#000000";
            ctx.textAlign = "center";
            ctx.textBaseline = "alphabetic";
            const fontSize = fitEngravingFontSize(engravingText, printW * (engraveMaxWidthPct / 100), printH * (engraveMaxHeightPct / 100), selectedFont.family, selectedFont.weight);
            ctx.font = `${selectedFont.weight} ${fontSize}px ${selectedFont.family}`;
            const printInk = inkMetrics(engravingText, fontSize, selectedFont.family, selectedFont.weight);
            ctx.fillText(engravingText, printW / 2, printH / 2 - printInk.inkFromBaseline);
        } else if (uploadedPhoto) {
            const rect = maskAreaRect();
            const k = printW / rect.width;
            const destW = uploadedPhoto.baseWidth * cropState.scale * k;
            const destH = uploadedPhoto.baseHeight * cropState.scale * k;
            const destX = printW / 2 - destW / 2 + cropState.x * k;
            const destY = printH / 2 - destH / 2 + cropState.y * k;
            ctx.drawImage(uploadedPhoto.image, destX, destY, destW, destH);
        } else if (selectedPattern) {
            drawCoverAt(ctx, selectedPattern.image, 0, 0, printW, printH);
        }

        // clip to the true product shape (cropped to its own bounding box, so the export
        // matches this design exactly rather than a generic circle)
        ctx.globalCompositeOperation = "destination-in";
        ctx.drawImage(maskAlphaCanvas, bx, by, bw, bh, 0, 0, printW, printH);
        ctx.globalCompositeOperation = "source-over";

        return canvas;
    }

    /************
     Canvas export - cart thumbnail mockup (all 3 layers: base photo, masked design,
     highlight/shadow overlay)
     ************/
    function renderMockupCanvas() {
        const mockupImg = activeMockup();
        const overlayImg = activeOverlay();
        const stackEl = activeVisual()?.querySelector(".pdpPersonalizeVisualStack");
        if (!stackEl) {
            throw new Error("Personalize visual isn't active for the current variant.");
        }
        const stackRect = stackEl.getBoundingClientRect();
        const maskRect = maskAreaRect(stackRect);
        const aspect = mockupImg.naturalHeight / mockupImg.naturalWidth;

        const canvas = document.createElement("canvas");
        canvas.width = MOCKUP_SIZE;
        canvas.height = Math.round(MOCKUP_SIZE * aspect);
        const ctx = canvas.getContext("2d");

        // layer 1: base mockup photo
        ctx.drawImage(mockupImg, 0, 0, canvas.width, canvas.height);

        // layer 2: design, composited on an offscreen canvas then clipped to the full mask
        const scaleX = canvas.width / stackRect.width;
        const scaleY = canvas.height / stackRect.height;
        const boundsX = (maskRect.left - stackRect.left) * scaleX;
        const boundsY = (maskRect.top - stackRect.top) * scaleY;
        const boundsW = maskRect.width * scaleX;
        const boundsH = maskRect.height * scaleY;

        const designLayer = document.createElement("canvas");
        designLayer.width = canvas.width;
        designLayer.height = canvas.height;
        const dctx = designLayer.getContext("2d");

        if (activeTab === "engrave") {
            dctx.fillStyle = "rgba(229, 231, 233, 0.8)";
            dctx.textAlign = "center";
            dctx.textBaseline = "alphabetic";
            const fontSize = fitEngravingFontSize(engravingText, boundsW * (engraveMaxWidthPct / 100), boundsH * (engraveMaxHeightPct / 100), selectedFont.family, selectedFont.weight);
            dctx.font = `${selectedFont.weight} ${fontSize}px ${selectedFont.family}`;
            const mockInk = inkMetrics(engravingText, fontSize, selectedFont.family, selectedFont.weight);
            dctx.fillText(engravingText, boundsX + boundsW / 2, boundsY + boundsH / 2 - mockInk.inkFromBaseline);
        } else if (uploadedPhoto) {
            const k = boundsW / maskRect.width;
            const destW = uploadedPhoto.baseWidth * cropState.scale * k;
            const destH = uploadedPhoto.baseHeight * cropState.scale * k;
            const destX = boundsX + boundsW / 2 - destW / 2 + cropState.x * k;
            const destY = boundsY + boundsH / 2 - destH / 2 + cropState.y * k;
            dctx.drawImage(uploadedPhoto.image, destX, destY, destW, destH);
        } else if (selectedPattern) {
            drawCoverAt(dctx, selectedPattern.image, boundsX, boundsY, boundsW, boundsH);
        }

        if (maskAlphaCanvas) {
            dctx.globalCompositeOperation = "destination-in";
            dctx.drawImage(maskAlphaCanvas, 0, 0, canvas.width, canvas.height);
            dctx.globalCompositeOperation = "source-over";
        }
        ctx.drawImage(designLayer, 0, 0);

        // layer 3: highlight/shadow overlay - design only, not on top of engraved text
        if (overlayImg && activeTab !== "engrave") {
            ctx.drawImage(overlayImg, 0, 0, canvas.width, canvas.height);
        }

        return canvas;
    }

    /************
     Cloudinary upload (unsigned)
     ************/
    function canvasToBlob(canvas) {
        return new Promise(resolve => canvas.toBlob(resolve, "image/png"));
    }

    // canvas.toBlob() writes no pHYs chunk, so every reader falls back to its default of 72 DPI -
    // which is what DoD's tooling was reporting. The pixels were always right; the label was
    // missing. This inserts a pHYs chunk declaring PRINT_DPI before the first IDAT, which costs
    // 21 bytes and leaves the image data untouched.
    const PRINT_DPI = 300;

    async function stampPngDensity(blob, dpi) {
        try {
            const bytes = new Uint8Array(await blob.arrayBuffer());
            const isPng = bytes.length > 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47;
            if (!isPng) return blob;

            const ppm = Math.round(dpi / 0.0254); // pHYs is pixels per metre
            const body = new Uint8Array(9);
            new DataView(body.buffer).setUint32(0, ppm);
            new DataView(body.buffer).setUint32(4, ppm);
            body[8] = 1; // unit specifier: metre

            const chunk = new Uint8Array(21);
            const view = new DataView(chunk.buffer);
            view.setUint32(0, 9);
            chunk.set([0x70, 0x48, 0x59, 0x73], 4); // "pHYs"
            chunk.set(body, 8);
            view.setUint32(17, crc32(chunk.subarray(4, 17)));

            // insert before the first IDAT (chunk layout: 4 len, 4 type, len data, 4 crc)
            let i = 8;
            while (i < bytes.length) {
                const len = new DataView(bytes.buffer, bytes.byteOffset + i, 4).getUint32(0);
                const type = String.fromCharCode(bytes[i + 4], bytes[i + 5], bytes[i + 6], bytes[i + 7]);
                if (type === "IDAT") break;
                i += 12 + len;
            }
            const out = new Uint8Array(bytes.length + chunk.length);
            out.set(bytes.subarray(0, i), 0);
            out.set(chunk, i);
            out.set(bytes.subarray(i), i + chunk.length);
            return new Blob([out], {type: "image/png"});
        } catch (err) {
            // a mislabelled file still prints correctly, so never fail the add to cart over this
            console.warn("Personalize: couldn't stamp print DPI", err);
            return blob;
        }
    }

    const CRC_TABLE = (() => {
        const t = new Uint32Array(256);
        for (let n = 0; n < 256; n++) {
            let c = n;
            for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
            t[n] = c >>> 0;
        }
        return t;
    })();

    function crc32(buf) {
        let c = 0xffffffff;
        for (let i = 0; i < buf.length; i++) c = CRC_TABLE[(c ^ buf[i]) & 0xff] ^ (c >>> 8);
        return (c ^ 0xffffffff) >>> 0;
    }

    async function uploadToCloudinary(blob, filename) {
        if (!cloudName || !uploadPreset) {
            throw new Error("Cloudinary is not configured for this section (missing cloud name or upload preset).");
        }
        const formData = new FormData();
        formData.append("file", blob, filename);
        formData.append("upload_preset", uploadPreset);

        const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
            method: "POST",
            body: formData,
        });
        if (!response.ok) {
            throw new Error(`Cloudinary upload failed (${response.status})`);
        }
        const data = await response.json();
        return data.secure_url;
    }

    /************
     Add to cart
     ************/
    function canSubmit() {
        if (activeTab === "engrave") return engraveSafe(engravingText).trim().length > 0;
        return !!(uploadedPhoto || selectedPattern);
    }

    personalizeATC?.addEventListener("click", async () => {
        if (!canSubmit()) return;

        // capture everything the submission needs right now, synchronously, before any
        // await - Cloudinary uploads below are network-bound and can take seconds, and
        // nothing should be re-read live after them: the variant (or the pattern/photo/
        // engraving) could otherwise change mid-flight and ship mismatched against
        // whichever mockup/mask was actually rendered and uploaded
        const variantIdAtSubmit = getCurrentVariantID();
        const mirrorAtSubmit = mirrorFor(variantIdAtSubmit);
        const activeTabAtSubmit = activeTab;
        const uploadedPhotoAtSubmit = uploadedPhoto;
        const selectedPatternAtSubmit = selectedPattern;
        const engravingTextAtSubmit = engraveSafe(engravingText);
        const selectedFontAtSubmit = selectedFont;
        const qualityIssuesAtSubmit = uploadedPhotoAtSubmit ? photoQualityIssues() : [];

        // fail loud rather than silently adding the plain, unpersonalized base variant. The
        // mirror carries the personalized price, the SKU, and - critically - the fulfillment
        // location, so there is no sensible fallback if it's missing. syncActiveVariant()
        // already hides the button in that state, so reaching here means the variant map and
        // the UI have drifted apart.
        // Fails closed on three separate ways the UI could be out of step with the variant
        // actually being submitted:
        //   - the variant isn't in the map at all (no mirror, no DoD approval, or no mockup)
        //   - the rendered mockup block belongs to a different variant, which means
        //     syncActiveVariant() has not caught up with however the variant was changed
        //     (product tabs and takeover CTAs select variants without firing the option
        //     inputs' change event that it listens to)
        //   - getCurrentVariantID() fell back to variants[0] because no option combination
        //     matched, which silently returns a variant the customer is not looking at
        const renderedVariantId = activeVisual()?.dataset.id;
        if (!mirrorAtSubmit || !renderedVariantId || renderedVariantId !== String(variantIdAtSubmit)) {
            alert("Personalization isn't available for this option. Please reselect your option and try again.");
            console.warn("Personalize: refused submit", {variantIdAtSubmit, renderedVariantId, inMap: !!mirrorAtSubmit});
            return;
        }

        personalizeATC.classList.add("loading");
        personalizeATC.disabled = true;
        // also lock Cancel: it's the only way to exit personalize mode and reach the
        // variant swatches while a submission is pending, and letting it "successfully"
        // reset state/exit while this promise chain is still running is exactly what
        // produces the mismatch above
        if (cancelBtn) cancelBtn.disabled = true;

        try {
            await document.fonts.ready;
            await maskReady;
            if (!maskAlphaCanvas) {
                throw new Error("Personalize mask isn't set up for this product yet.");
            }

            const mockupCanvas = renderMockupCanvas();
            let mockupBlob = await canvasToBlob(mockupCanvas);

            // Engraving goes out as vector; Design (patterns and photos) is inherently raster.
            // printFormat is recorded on the line item so a fallback is visible to DoD rather
            // than silently arriving as a raster in their vector workflow.
            let printBlob;
            let printExt = "png";
            let printFormat = "png";

            if (activeTabAtSubmit === "engrave") {
                try {
                    const geo = printGeometry(mirrorAtSubmit);
                    const otFont = await loadOutlineFont(selectedFontAtSubmit.file);
                    const svg = buildEngraveSvg(otFont, engravingTextAtSubmit.trim(), selectedFontAtSubmit, geo);
                    printBlob = new Blob([svg], {type: "image/svg+xml"});
                    printExt = "svg";
                    printFormat = "svg";
                } catch (err) {
                    // never lose the order over the export format - a raster engraving still
                    // prints, it just costs DoD a raster pass instead of a path trace
                    console.error("Personalize: outlined SVG failed, falling back to raster", err);
                }
            }

            if (!printBlob) {
                printBlob = await stampPngDensity(await canvasToBlob(renderPrintCanvas(mirrorAtSubmit)), PRINT_DPI);
            }

            const stamp = Date.now();
            const [printUrl, previewUrl] = await Promise.all([
                uploadToCloudinary(printBlob, `personalize-print-${stamp}.${printExt}`),
                uploadToCloudinary(mockupBlob, `personalize-preview-${stamp}.png`),
            ]);

            // Customer-visible: just what they chose - the pattern's name, "Custom Upload",
            // or the engraving text itself. Everything the customer doesn't need is hidden
            // (underscore-prefixed) but still lands on the order for the print team.
            //
            // _design_type is the grouping key for reporting. It's worth storing even though
            // the mode feels obvious from the visible keys: one mirror SKU covers Design and
            // Engrave alike, so the SKU can't tell them apart, and pattern-vs-upload are both
            // "design" no matter how you slice the SKU. _pattern_handle is the metaobject's
            // stable id, so renaming a pattern in Admin doesn't split its sales history.
            const properties = {};
            if (activeTabAtSubmit === "design") {
                if (uploadedPhotoAtSubmit) {
                    properties.Design = "Custom Upload";
                    properties._design_type = "upload";
                } else {
                    properties.Design = selectedPatternAtSubmit.name;
                    properties._design_type = "pattern";
                    properties._pattern_handle = selectedPatternAtSubmit.handle;
                }
            } else {
                properties.Engraving = engravingTextAtSubmit.trim();
                properties._design_type = "engraving";
                properties._font = selectedFontAtSubmit.label;
            }
            properties._print_file = printUrl;
            properties._print_format = printFormat;
            properties._preview_image = previewUrl;
            // for the print team: what the quality check flagged, if anything (see photoQualityIssues)
            if (activeTabAtSubmit === "design" && qualityIssuesAtSubmit.length) {
                properties._quality_warning = qualityIssuesAtSubmit.join(", ");
            }

            // one line item: the mirror variant, already priced to include personalization.
            // There's no separate fee line to keep in sync any more, and no base-variant line -
            // the mirror is the thing being sold.
            await addProduct({items: [{quantity: 1, id: mirrorAtSubmit.id, properties}]});
            await refreshCart();
            await openCart();

            exitPersonalize();
        } catch (err) {
            console.error("Personalize add to cart failed:", err);
            alert("Something went wrong uploading your design. Please try again.");
        } finally {
            personalizeATC.classList.remove("loading");
            personalizeATC.disabled = false;
            if (cancelBtn) cancelBtn.disabled = false;
        }
    });

    /************
     Init
     ************/
    syncActiveVariant();

    // deep link: ?personalize opens the customizer on load, e.g. for ads and emails. Pair it
    // with ?variant= for a personalizable color - enterPersonalize() ignores the request when
    // the selected variant has no Personalize button, so the page just loads normally.
    if (new URLSearchParams(window.location.search).has("personalize")) {
        enterPersonalize();
    }
}());
